<script setup>
/**
 * The questions a member is asked about a competition, one compact row each.
 *
 * Renders WHATEVER the API returns (useCompetitionQuestions.js): per question
 * a short label beside a switch that starts OFF (leaving it is a no), and a
 * small "More info" that opens the few lines of details under the row
 * (owner, 25 Sep 2026: the old heading, intro and bullets per question were
 * too much, worst with several stacked). It holds no copy of its own for any
 * question, which is what lets the API add one without a website release.
 *
 * Used by the first-view dialog (CompetitionQuestionsPrompt) and inline on
 * the join confirmation (/invite/[slug]). v-model is {key: true|false}.
 */
import {ref} from 'vue';

defineProps({
  questions: {type: Array, default: () => []},
  modelValue: {type: Object, default: () => ({})},
  disabled: {type: Boolean, default: false},
});
const emit = defineEmits(['update:modelValue']);

const set = (current, key, value) => emit('update:modelValue', {...current, [key]: value === true});

const open = ref({});
const toggleInfo = (key) => { open.value = {...open.value, [key]: !open.value[key]}; };
</script>

<template>
  <div class="cql">
    <div v-for="q in questions" :key="q.key" class="cql-q" :data-question="q.key">
      <div class="cql-row">
        <label class="cql-label" :for="`cq-${q.key}`">{{ q.label }}</label>
        <v-switch
            :id="`cq-${q.key}`"
            :model-value="modelValue?.[q.key] === true"
            :disabled="disabled"
            :aria-label="q.label"
            density="compact"
            hide-details
            inset
            color="primary"
            class="cql-switch"
            @update:model-value="(v) => set(modelValue, q.key, v)"
        />
      </div>
      <button v-if="q.details?.length" type="button" class="cql-more" :aria-expanded="open[q.key] === true"
              :data-more="q.key" @click="toggleInfo(q.key)">
        More info
        <v-icon size="16" :icon="open[q.key] ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
      </button>
      <ul v-if="open[q.key] && q.details?.length" class="cql-details">
        <li v-for="(line, i) in q.details" :key="i">{{ line }}</li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cql { display: flex; flex-direction: column; }
.cql-q { padding: 8px 0; border-bottom: 1px solid rgb(var(--v-theme-borderLight)); }
.cql-q:first-child { padding-top: 0; }
.cql-q:last-child { border-bottom: 0; padding-bottom: 0; }
.cql-row { display: flex; align-items: center; gap: 12px; }
.cql-label { flex: 1; min-width: 0; font-size: 14.5px; font-weight: 600; line-height: 1.35; cursor: pointer; }
.cql-switch { flex: 0 0 auto; }
.cql-more {
  display: inline-flex; align-items: center; gap: 2px;
  padding: 0; margin-top: 2px; background: none; border: 0;
  font-size: 13px; font-weight: 600; color: rgb(var(--v-theme-primary)); cursor: pointer;
}
.cql-details { margin: 6px 0 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; font-size: 13px; line-height: 1.45; opacity: .85; }
</style>
